import React, { useMemo, useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Link, useNavigate } from "react-router-dom";
import "./Blog.css";
import { useTranslation } from "react-i18next";
import { newsApi } from "../api/newsApi";

// Detail səhifən səndə /xeber/:id-dir
const NEWS_DETAIL_BASE = "/xeber";

export default function Blog() {
  const [q, setQ] = useState("");
  const [visible, setVisible] = useState(8);
  const brandColor = "#002244";
  const nav = useNavigate();
  const { t, i18n } = useTranslation();

  const [items, setItems] = useState([]);

  const pick = (n, base) => {
    const lang = (i18n?.language || "az").toLowerCase();
    const suffix = lang.startsWith("en") ? "En" : lang.startsWith("ru") ? "Ru" : "Az";

    const keyPascal = `${base}${suffix}`; // TitleAz / ContentEn
    const keyCamel = `${base[0].toLowerCase() + base.slice(1)}${suffix}`; // titleAz / contentEn

    const fallbackCamelAz = `${base[0].toLowerCase() + base.slice(1)}Az`;
    const fallbackPascalAz = `${base}Az`;

    return n?.[keyCamel] ?? n?.[keyPascal] ?? n?.[fallbackCamelAz] ?? n?.[fallbackPascalAz] ?? "";
  };

  const firstN = (text, n = 80) => {
    const s = (text || "").toString().trim();
    if (!s) return "";
    return s.length > n ? s.slice(0, n) + "…" : s;
  };

  // dd.MM.yyyy
  const formatDMY = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  // ✅ new fields (camel/pascal fallback)
  const getManualDate = (n) => n?.manualInsertedDate ?? n?.ManualInsertedDate ?? null;
  const getHasImage = (n) => n?.hasImage ?? n?.HasImage ?? false;
  const getId = (n) => n?.id ?? n?.Id;

  useEffect(() => {
    let ok = true;
    const load = async () => {
      try {
        const res = await newsApi.getAll();
        if (!ok) return;

        // istəsən burda publish tarixinə görə də sort edə bilərik
        const list = res.data || [];
        setItems(list);
      } catch (e) {
        console.error("blog(news) load error:", e);
        setItems([]);
      }
    };
    load();
    return () => {
      ok = false;
    };
  }, []);

  const posts = useMemo(() => {
    return (items || []).map((n) => {
      const id = getId(n);
      const title = pick(n, "Title") || t("news_default");
      const content = pick(n, "Content") || "";
      const manualDate = getManualDate(n);

      return {
        id,
        title,
        excerpt: firstN(content, 90), // ✅ short yoxdur, content-dən çıxarırıq
        content,
        date: manualDate ? formatDMY(manualDate) : "",
        image: getHasImage(n) ? newsApi.getImageUrl(id) + `?t=${Date.now()}` : "/images/news1.jpg",
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, i18n.language]);

  const goDetail = (id) => nav(`/xeber/${id}`);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return posts;

    return posts.filter((p) =>
      (p.title + " " + p.excerpt + " " + p.content + " " + p.date).toLowerCase().includes(s)
    );
  }, [q, posts]);

  return (
    <Layout>
      <section className="section blogPage" style={{ ["--brand"]: brandColor }}>
        <div className="container">
          <div className="sectionHead mb-3">
            <h1 className="hTitle mb-1">{t("blog_title")}</h1>
            <p className="pMuted mb-0">{t("blog_subtitle")}</p>
          </div>

          {/* Search */}
          <div className="card blogSearchCard mb-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-12">
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setVisible(8);
                  }}
                  className="form-control blogSearch"
                  placeholder={t("search_placeholder")}
                />
              </div>
            </div>
          </div>

          {/* Grid */}
          <div className="row g-3">
            {filtered.slice(0, visible).map((p) => (
              <div key={p.id} className="col-12 col-sm-6 col-lg-3">
                <article className="blogCard h-100 blogCardTall">
                  <Link to={`${NEWS_DETAIL_BASE}/${p.id}`} className="blogImgLink">
                    <div className="blogImgWrap blogImgWrapTall">
                      <img
                        className="blogImg"
                        src={p.image}
                        alt={p.title}
                        onError={(e) => {
                          e.currentTarget.src = "/images/news1.jpg";
                        }}
                      />
                    </div>
                  </Link>

                  <div className="blogBody">
                    <Link to={`${NEWS_DETAIL_BASE}/${p.id}`} className="blogTitleLink">
                      <div className="blogTitle">{p.title}</div>
                    </Link>

                    <div className="blogExcerpt blogExcerptTall">{p.excerpt}</div>

                    {/* ✅ əl ilə daxil edilən tarix */}
                    {p.date ? <div className="blogDateText"> 📅 {p.date}</div> : null}

                    <div className="blogActions">
                      <button type="button" className="btn btnBrand w-100" onClick={() => goDetail(p.id)}>
                        {t("read_more")}
                      </button>
                    </div>
                  </div>
                </article>
              </div>
            ))}

            {filtered.length === 0 ? (
              <div className="col-12">
                <div className="card">
                  <div className="cardBody" style={{ padding: 16 }}>
                    {t("no_news_found")}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Load more */}
          {visible < filtered.length && (
            <div className="text-center mt-4">
              <button type="button" className="btn btnBrand px-4 py-3" onClick={() => setVisible((v) => v + 8)}>
                {t("show_more")}
              </button>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
